import { FunctionComponent, useEffect } from 'react';
import {
    Box,
    Fab,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
    Zoom,
} from '@mui/material';
import ArrowLeftIcon from '@heroicons/react/24/solid/ArrowLeftIcon';
import { useSearchParams } from 'react-router-dom';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    useDeleteProductConfigurationMutation,
    useGetProductConfigurationQuery,
} from '@/api/v2/productConfigurations';
import Link from '@mui/material/Link';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { useNotification } from '@starjack/features/notifications';
import { ProductConfigurationGeneralConfiguration } from '@/pages/productConfiguration/components/ProductConfigurationDetail/ProductConfigurationGeneralConfiguration';
import { ProductPageDetails } from '@/pages/productConfiguration/components/ProductPageDetails';

export const ProductConfigurationDetailContent: FunctionComponent = () => {
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const {
        actions: { setPageData, setExplanationVideo },
    } = useSlice(pageDataSlice, 'requestSlice');
    const { data: productConfiguration } = useGetProductConfigurationQuery({
        liftOperatorId,
        productId: id as string,
    });
    const [deleteProductConfiguration] =
        useDeleteProductConfigurationMutation();

    const { addNotification } = useNotification();

    const resetProductConfiguration = () => {
        deleteProductConfiguration({
            liftOperatorId,
            productConfiguration,
        })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Produktkonfiguration wurde zurückgesetzt',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Zurücksetzen. Bitte später noch einmal versuchen.',
                });
            });
    };

    useEffect(() => {
        setPageData({
            title: 'Produktkonfiguration Details',
            body: <ProductPageDetails />,
        });
        setExplanationVideo(
            'https://my-starjack.fleeq.io/l/vvs11f19di-t7m2ptksa0'
        );
    }, []);

    return (
        <>
            <Link
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.primary',
                    mb: 6,
                }}
                href='/produktkonfiguration'
            >
                <SvgIcon
                    fontSize='small'
                    sx={{
                        mr: 2,
                    }}
                >
                    <ArrowLeftIcon />
                </SvgIcon>

                <Typography variant='subtitle2' component='span'>
                    Produktkonfiguration
                </Typography>
            </Link>

            {productConfiguration && (
                <Stack
                    sx={{
                        alignItems: 'flex-start',
                    }}
                    spacing={theme.page.verticalSpacing}
                >
                    <Box>
                        <Typography variant='h4'>
                            Konfiguration für alle Tickets mit hinterlegter
                            Ticketbezeichnung:{' '}
                        </Typography>
                        <Typography
                            variant='h4'
                            sx={(theme) => ({
                                color: theme.palette.primary.main,
                                mt: 2,
                                ml: 2,
                            })}
                        >
                            {productConfiguration.product?.name.de}
                        </Typography>
                    </Box>

                    <ProductConfigurationGeneralConfiguration
                        baseConfiguration={productConfiguration}
                    />
                </Stack>
            )}

            <Zoom in={!!productConfiguration?.id} unmountOnExit>
                <Fab
                    onClick={resetProductConfiguration}
                    sx={{
                        position: 'fixed',
                        right: 32,
                        bottom: 32,
                        bgcolor: 'error.dark',
                    }}
                    variant='extended'
                    color='error'
                >
                    Konfiguration auf Standard zurücksetzen
                </Fab>
            </Zoom>
        </>
    );
};
